import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, CheckCircle, XCircle, Loader, SwitchCamera } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/library';
import api from '../api/axios';
import { Card } from './shared/Card';
import { Button } from './shared/Button';
import { toast } from 'react-toastify';

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 'var(--space-4)',
  },
  scanner: {
    maxWidth: '600px',
    width: '100%',
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-xl)',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-2xl)',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: 'var(--space-6)',
    background: 'var(--gradient-premium)',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  title: {
    /* `color: inherit` a propósito.
     *
     * `src/index.css` tiene `h1, h2, …, h6 { color: var(--text) }`, y una
     * regla de hoja sobre el propio elemento gana a la herencia del
     * `color: white` que el encabezado pone en línea. En tema claro
     * `--text` es `#0A1628`, que es exactamente la primera parada del
     * degradado del encabezado: el título quedaba del mismo color que su
     * fondo, con contraste 1:1, invisible. El subtítulo no se veía afectado
     * porque `p` no lleva color en la hoja global. */
    color: "inherit",
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-xl)',
    fontWeight: '700',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  closeButton: {
    padding: '0.5rem',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  switchButton: {
    padding: '0.5rem',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    height: '400px',
    backgroundColor: '#000',
    flex: '1',
    minHeight: '300px',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  overlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '250px',
    height: '250px',
    border: '3px solid var(--gold)',
    borderRadius: 'var(--radius-lg)',
    pointerEvents: 'none',
  },
  instructions: {
    position: 'absolute',
    bottom: '1rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-sm)',
    textAlign: 'center',
  },
  result: {
    padding: 'var(--space-8)',
    textAlign: 'center',
    overflowY: 'auto',
  },
  resultIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 1rem',
  },
  resultTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-xl)',
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  resultMessage: {
    fontSize: 'var(--text-base)',
    color: 'var(--text-secondary)',
    marginBottom: '1.5rem',
  },
  visitInfo: {
    backgroundColor: 'var(--bg-secondary)',
    padding: '1rem',
    borderRadius: 'var(--radius-lg)',
    marginBottom: '1.5rem',
    textAlign: 'left',
  },
  visitRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: 'var(--text-sm)',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 'var(--space-12)',
    gap: 'var(--space-4)',
  },
  error: {
    padding: 'var(--space-8)',
    textAlign: 'center',
    color: 'var(--danger)',
  },
  // Responsive styles
  '@media (max-width: 768px)': {
    container: {
      padding: 0,
    },
    scanner: {
      maxWidth: '100%',
      width: '100%',
      height: '100%',
      maxHeight: '100%',
      borderRadius: 0,
    },
    header: {
      padding: 'var(--space-4)',
    },
    videoContainer: {
      height: 'auto',
      minHeight: '50vh',
    },
    result: {
      padding: 'var(--space-4)',
    },
    loading: {
      padding: 'var(--space-6)',
    },
    error: {
      padding: 'var(--space-4)',
    },
  },
};

export default function QRScanner({ onClose, onScanSuccess }) {
  const [scanning, setScanning] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [facingBack, setFacingBack] = useState(true);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  /* EL PLAN B DE LA CASETA
   *
   * La camara puede fallar, el permiso puede estar denegado, o el telefono del
   * visitante puede estar apagado. Sin una segunda via, un fallo de camara
   * deja la caseta sin forma de admitir a nadie que no este en la lista del
   * dia —y un pase permanente o recurrente no lo esta—.
   *
   * El campo se ensena SIEMPRE, no solo cuando la camara ya fallo: un guardia
   * que descubre el plan B en el momento en que lo necesita, con alguien
   * esperando delante, ya perdio el tiempo que el plan B existia para
   * ahorrarle. */
  const [codigo, setCodigo] = useState('');
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Detectar si hay múltiples cámaras
    navigator.mediaDevices.enumerateDevices().then(devices => {
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    }).catch(() => {});

    startScanning(true);
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async (useBackCamera) => {
    try {
      stopScanning();
      await new Promise(resolve => setTimeout(resolve, 300));

      // Obtener stream con facingMode - más confiable que deviceId
      const constraints = {
        video: {
          facingMode: useBackCamera ? { ideal: 'environment' } : { ideal: 'user' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      codeReader.decodeFromStream(
        stream,
        videoRef.current,
        (result, err) => {
          if (result) {
            handleQRCode(result.getText());
          }
          if (err && err.name !== 'NotFoundException') {
            console.error('QR Scanner error:', err);
          }
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      if (err.name === 'NotAllowedError') {
        setError('Permiso de cámara denegado. Habilita el acceso a la cámara en la configuración del navegador.');
      } else if (err.name === 'NotReadableError') {
        setError('La cámara está siendo usada por otra aplicación. Cierra otras apps que usen la cámara e intenta de nuevo.');
      } else {
        setError('Error al iniciar la cámara. Verifica los permisos.');
      }
    }
  };

  const switchCamera = () => {
    const newFacing = !facingBack;
    setFacingBack(newFacing);
    startScanning(newFacing);
  };

  const stopScanning = () => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleQRCode = async (qrData) => {
    if (loading) return; // Evitar múltiples escaneos
    
    setLoading(true);
    setScanning(false);
    stopScanning();

    try {
      // Parsear la URL del QR
      const url = new URL(qrData);
      const pathParts = url.pathname.split('/');
      const qrCode = pathParts[pathParts.length - 1];
      const token = url.searchParams.get('token');

      if (!qrCode || !token) {
        throw new Error('Código QR inválido');
      }

      // Validar el QR con el backend
      const response = await api.post('/visits/qr/validate', {
        qrCode,
        token
      });

      mostrarResultado(response.data);
    } catch (err) {
      console.error('Error validating QR:', err);
      mostrarError(err);
    } finally {
      setLoading(false);
    }
  };

  /** Lo que se hace con la respuesta del servidor, venga del escaner o del
   * codigo teclado. Vivia dentro de `handleQRCode`; se saco aqui para que las
   * dos vias den exactamente el mismo resultado en pantalla. */
  const mostrarResultado = (datos) => {
    setResult({
      success: true,
      action: datos.action,
      message: datos.message,
      visit: datos.visit,
    });
    toast.success(datos.message);
    if (onScanSuccess) onScanSuccess(datos);
    setTimeout(() => onClose(), 3000);
  };

  const mostrarError = (err) => {
    const mensaje =
      err.response?.data?.message || err.message || 'Error al validar el pase';
    setResult({ success: false, message: mensaje });
    toast.error(mensaje);
  };

  const validarCodigo = async (e) => {
    if (e) e.preventDefault();
    const limpio = codigo.trim();
    if (limpio.length < 6) {
      toast.error('El codigo del pase tiene 12 caracteres');
      return;
    }
    setEnviandoCodigo(true);
    stopScanning();
    setScanning(false);
    try {
      const { data } = await api.post('/visits/qr/validate', { shortCode: limpio });
      mostrarResultado(data);
    } catch (err) {
      mostrarError(err);
    } finally {
      setEnviandoCodigo(false);
    }
  };

  /** El campo para teclear el codigo. Se usa en dos sitios: debajo del video,
   * como alternativa, y en el estado de error, donde pasa a ser la via
   * principal porque la camara ya no esta disponible. */
  const campoCodigo = (destacado) => (
    <form onSubmit={validarCodigo} style={{ marginTop: destacado ? '1.25rem' : '0.75rem' }}>
      <label
        style={{
          display: 'block',
          fontSize: '0.8125rem',
          color: destacado ? 'var(--text-primary)' : 'rgba(255,255,255,0.75)',
          marginBottom: '0.5rem',
          fontWeight: destacado ? 600 : 400,
        }}
      >
        {destacado
          ? 'Escriba el codigo que lleva impreso el pase'
          : 'O escriba el codigo del pase'}
      </label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={codigo}
          onChange={(ev) => setCodigo(ev.target.value)}
          placeholder="a1b2c3d4e5f6"
          autoComplete="off"
          spellCheck={false}
          maxLength={12}
          aria-label="Codigo del pase"
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: '44px',
            fontSize: '16px',
            letterSpacing: '0.08em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            textTransform: 'uppercase',
            padding: '0 0.75rem',
            borderRadius: 'var(--radius-md, 8px)',
            border: '1px solid rgba(255,255,255,0.3)',
            background: destacado ? 'var(--bg-secondary, #fff)' : 'rgba(255,255,255,0.12)',
            color: destacado ? 'var(--text-primary)' : '#fff',
          }}
        />
        <Button type="submit" loading={enviandoCodigo} disabled={enviandoCodigo}>
          Validar
        </Button>
      </div>
    </form>
  );

  const handleRetry = () => {
    setResult(null);
    setError(null);
    setScanning(true);
    startScanning(facingBack);
  };

  return (
    <div style={{
      ...styles.container,
      ...(isMobile ? styles['@media (max-width: 768px)'].container : {})
    }} onClick={onClose}>
      <div style={{
        ...styles.scanner,
        ...(isMobile ? styles['@media (max-width: 768px)'].scanner : {})
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          ...styles.header,
          ...(isMobile ? styles['@media (max-width: 768px)'].header : {})
        }}>
          <h2 style={styles.title}>Escanear Código QR</h2>
          <div style={styles.headerActions}>
            {hasMultipleCameras && scanning && (
              <button
                onClick={switchCamera}
                style={styles.switchButton}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                title="Cambiar cámara"
              >
                <SwitchCamera size={24} />
              </button>
            )}
            <button
              onClick={onClose}
              style={styles.closeButton}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {error ? (
          <div style={{
            ...styles.error,
            ...(isMobile ? styles['@media (max-width: 768px)'].error : {})
          }}>
            <p>{error}</p>
            <Button onClick={handleRetry} style={{ marginTop: '1rem' }}>
              Reintentar
            </Button>
            {campoCodigo(true)}
          </div>
        ) : loading ? (
          <div style={{
            ...styles.loading,
            ...(isMobile ? styles['@media (max-width: 768px)'].loading : {})
          }}>
            <div className="animate-spin">
              <Loader size={48} />
            </div>
            <p>Validando código QR...</p>
          </div>
        ) : result ? (
          <div style={{
            ...styles.result,
            ...(isMobile ? styles['@media (max-width: 768px)'].result : {})
          }}>
            {result.success ? (
              <>
                <CheckCircle size={64} style={{ ...styles.resultIcon, color: 'var(--success)' }} />
                <h3 style={{ ...styles.resultTitle, color: 'var(--success)' }}>{result.message}</h3>
                {result.visit && (
                  <div style={styles.visitInfo}>
                    <div style={styles.visitRow}>
                      <span>Visitante:</span>
                      <strong>{result.visit.visitorName}</strong>
                    </div>
                    <div style={styles.visitRow}>
                      <span>Destino:</span>
                      <strong>Polígono {result.visit.polygon} - Casa {result.visit.houseNumber}</strong>
                    </div>
                    <div style={styles.visitRow}>
                      <span>Acción:</span>
                      <strong>{result.action === 'entry' ? 'Entrada registrada' : 'Salida registrada'}</strong>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <XCircle size={64} style={{ ...styles.resultIcon, color: 'var(--danger)' }} />
                <h3 style={{ ...styles.resultTitle, color: 'var(--danger)' }}>Error</h3>
                <p style={styles.resultMessage}>{result.message}</p>
                <Button onClick={handleRetry}>Escanear otro código</Button>
              </>
            )}
          </div>
        ) : scanning ? (
          <div style={{
            ...styles.videoContainer,
            ...(isMobile ? styles['@media (max-width: 768px)'].videoContainer : {})
          }}>
            <video ref={videoRef} style={styles.video} />
            <div style={styles.overlay} />
            <div style={styles.instructions}>
              Coloca el código QR dentro del recuadro
            </div>
          </div>
        ) : null}

        {!result && !loading && !error && (
          <div style={{ padding: '0 1.25rem 1.25rem' }}>
            {campoCodigo(false)}
          </div>
        )}
      </div>
    </div>
  );
}